import React, { useState, useEffect } from 'react';
import { requestOTP, verifyOTP } from '../services/api';

const NAVY = '#1f4e79';
const ORANGE = '#f26522';

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=Inter:wght@400;500;600&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: 'Inter', sans-serif; -webkit-font-smoothing: antialiased; }

  .login-root {
    min-height: 100vh;
    display: grid;
    grid-template-columns: 1fr 1fr;
  }

  /* ── LEFT PANEL ── */
  .login-left {
    background: linear-gradient(160deg, ${NAVY} 0%, #0d2d52 60%, #07152a 100%);
    display: flex; flex-direction: column;
    justify-content: space-between;
    padding: 32px 52px;
    position: relative; overflow: hidden;
  }
  .login-left::before {
    content: '';
    position: absolute; top: -150px; right: -150px;
    width: 500px; height: 500px; border-radius: 50%;
    background: radial-gradient(circle, rgba(242,101,34,0.15) 0%, transparent 65%);
    pointer-events: none;
  }
  .login-left::after {
    content: '';
    position: absolute; bottom: -150px; left: -100px;
    width: 400px; height: 400px; border-radius: 50%;
    background: radial-gradient(circle, rgba(255,255,255,0.04) 0%, transparent 65%);
    pointer-events: none;
  }
  .left-logo {
    position: relative; z-index: 1;
  }
  .left-logo img {
    height: 150px; width: auto; object-fit: contain;
    filter: None !important;
  }
  }
  .left-content {
    position: relative; z-index: 1;
  }
  .left-tag {
    display: inline-flex; align-items: center; gap: 8px;
    background: rgba(242,101,34,0.15);
    border: 1px solid rgba(242,101,34,0.25);
    padding: 6px 14px; border-radius: 20px; margin-bottom: 28px;
  }
  .left-tag-dot {
    width: 7px; height: 7px; border-radius: 50%;
    background: ${ORANGE}; animation: glow 2s infinite;
  }
  @keyframes glow {
    0%,100% { box-shadow: 0 0 0 2px rgba(242,101,34,0.2); }
    50% { box-shadow: 0 0 0 5px rgba(242,101,34,0.1); }
  }
  .left-tag-text { font-size: 12px; font-weight: 600; color: ${ORANGE}; letter-spacing: 0.5px; }
  .left-h2 {
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 38px; font-weight: 800; color: #fff;
    line-height: 1.15; letter-spacing: -1px; margin-bottom: 16px;
  }
  .left-h2 span { color: ${ORANGE}; }
  .left-sub {
    font-size: 15px; color: rgba(255,255,255,0.55);
    line-height: 1.75; margin-bottom: 40px; max-width: 340px;
  }
  .left-features { list-style: none; display: flex; flex-direction: column; gap: 14px; }
  .left-feat {
    display: flex; align-items: center; gap: 12px;
    font-size: 14px; color: rgba(255,255,255,0.70);
  }
  .feat-check {
    width: 24px; height: 24px; border-radius: 50%; flex-shrink: 0;
    background: rgba(242,101,34,0.15);
    display: flex; align-items: center; justify-content: center;
    color: ${ORANGE}; font-size: 12px; font-weight: 700;
  }
  .left-footer {
    position: relative; z-index: 1;
    font-size: 12px; color: rgba(255,255,255,0.25);
  }

  /* ── RIGHT PANEL ── */
  .login-right {
    background: #f8fafc;
    display: flex; align-items: center; justify-content: center;
    padding: 48px 40px;
  }
  .login-card {
    background: #fff;
    border: 1px solid rgba(31,78,121,0.10);
    border-radius: 24px;
    padding: 44px 40px;
    width: 100%; max-width: 400px;
    box-shadow: 0 8px 48px rgba(31,78,121,0.10), 0 1px 3px rgba(0,0,0,0.04);
  }
  .card-header { margin-bottom: 32px; }
  .card-title {
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 24px; font-weight: 800; color: #0f172a;
    margin-bottom: 6px; letter-spacing: -0.5px;
  }
  .card-sub { font-size: 14px; color: #64748b; line-height: 1.6; }
  .card-sub strong { color: ${NAVY}; font-weight: 600; }

  /* Step indicator */
  .step-indicator {
    display: flex; align-items: center; gap: 8px; margin-bottom: 28px;
  }
  .step-dot {
    width: 8px; height: 8px; border-radius: 50%;
    background: #e2e8f0; transition: all 0.3s;
  }
  .step-dot.active { background: ${ORANGE}; transform: scale(1.3); }
  .step-dot.done { background: #22c55e; }
  .step-label { font-size: 12px; color: #94a3b8; margin-left: 4px; }

  /* Form elements */
  .field-label {
    display: block; font-size: 13px; font-weight: 600;
    color: #374151; margin-bottom: 8px;
  }
  .input-wrap {
    position: relative; margin-bottom: 8px;
  }
  .input-prefix-flag {
    position: absolute; left: 14px; top: 50%; transform: translateY(-50%);
    font-size: 14px; font-weight: 600; color: ${NAVY};
    display: flex; align-items: center; gap: 6px; pointer-events: none;
  }
  .input-divider {
    width: 1px; height: 18px; background: #e2e8f0; margin: 0 2px;
  }
  .text-input {
    width: 100%; padding: 14px 16px 14px 80px;
    border: 1.5px solid #e2e8f0; border-radius: 12px;
    font-family: 'Inter', sans-serif; font-size: 15px; color: #0f172a;
    background: #f8fafc; outline: none; transition: all 0.2s;
  }
  .text-input:focus {
    border-color: ${ORANGE}; background: #fff;
    box-shadow: 0 0 0 3px rgba(242,101,34,0.10);
  }
  .text-input::placeholder { color: #94a3b8; }
  .text-input.otp-input {
    padding: 14px 16px; text-align: center;
    font-size: 28px; font-weight: 700; letter-spacing: 12px;
    color: ${NAVY}; font-family: 'Plus Jakarta Sans', sans-serif;
  }

  /* OTP timer */
  .otp-timer {
    text-align: center; font-size: 12px; margin-bottom: 20px;
    padding: 8px; border-radius: 8px;
  }
  .otp-timer.active { color: #64748b; background: #f8fafc; }
  .otp-timer.warning { color: #dc2626; background: #fef2f2; }
  .otp-timer.expired { color: #dc2626; font-weight: 600; background: #fef2f2; }

  /* Buttons */
  .btn-primary {
    width: 100%; padding: 15px;
    background: linear-gradient(135deg, ${ORANGE}, #e05a1a);
    color: #fff; border: none; cursor: pointer;
    border-radius: 12px; font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 15px; font-weight: 700; letter-spacing: 0.2px;
    transition: all 0.25s cubic-bezier(0.4,0,0.2,1);
    box-shadow: 0 4px 16px rgba(242,101,34,0.30);
    margin-bottom: 10px;
  }
  .btn-primary:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(242,101,34,0.40);
  }
  .btn-primary:disabled {
    background: #cbd5e1; box-shadow: none; cursor: not-allowed;
  }
  .btn-secondary {
    width: 100%; padding: 12px;
    background: #f8fafc; border: 1.5px solid #e2e8f0;
    color: ${NAVY}; border-radius: 12px;
    font-family: 'Inter', sans-serif; font-size: 13px; font-weight: 600;
    cursor: pointer; transition: all 0.2s; margin-bottom: 8px;
  }
  .btn-secondary:hover:not(:disabled) { background: #f1f5f9; border-color: #cbd5e1; }
  .btn-secondary:disabled { color: #94a3b8; cursor: not-allowed; }
  .btn-ghost {
    width: 100%; padding: 10px;
    background: none; border: none;
    color: #64748b; font-size: 13px; cursor: pointer;
    transition: color 0.2s; font-family: 'Inter', sans-serif;
  }
  .btn-ghost:hover { color: ${NAVY}; }

  /* Divider */
  .divider {
    border: none; border-top: 1px solid #f1f5f9;
    margin: 20px 0;
  }
  .register-row {
    text-align: center; font-size: 13px; color: #64748b;
  }
  .register-link {
    background: none; border: none; cursor: pointer;
    color: ${ORANGE}; font-size: 13px; font-weight: 700;
    padding: 0; transition: color 0.2s;
  }
  .register-link:hover { color: #e05a1a; }

  /* Error */
  .error-box {
    background: #fef2f2; border: 1px solid #fecaca;
    border-radius: 10px; padding: 12px 16px;
    color: #dc2626; font-size: 13px; text-align: center;
    margin-top: 12px; line-height: 1.5;
  }

  /* WhatsApp OTP hint */
  .wa-hint {
    display: flex; align-items: center; gap: 8px;
    background: #f0fdf4; border: 1px solid #bbf7d0;
    border-radius: 10px; padding: 10px 14px;
    margin-bottom: 20px;
  }
  .wa-hint-icon { font-size: 18px; flex-shrink: 0; }
  .wa-hint-text { font-size: 12px; color: #166534; line-height: 1.5; }

  /* Responsive */
  @media (max-width: 768px) {
    .login-root { grid-template-columns: 1fr; }
    .login-left { display: none; }
    .login-right { padding: 24px 20px; background: linear-gradient(160deg, ${NAVY} 0%, #0d2d52 100%); }
    .login-card { border-radius: 20px; padding: 32px 24px; }
  }
`;

export default function Login({ onLogin, onRegister }) {
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [step, setStep] = useState('phone');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [countdown, setCountdown] = useState(0);
  const [resendLoading, setResendLoading] = useState(false);

  useEffect(() => {
    if (countdown <= 0) return;
    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) { clearInterval(timer); return 0; }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [countdown]);

  const handleRequestOTP = async () => {
    if (phone.length !== 10) { setError('Enter a valid 10-digit phone number'); return; }
    setLoading(true); setError('');
    try {
      await requestOTP(phone);
      setStep('otp'); setCountdown(600);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send OTP');
    } finally { setLoading(false); }
  };

  const handleResendOTP = async () => {
    setResendLoading(true); setError(''); setOtp('');
    try {
      await requestOTP(phone); setCountdown(600);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to resend OTP');
    } finally { setResendLoading(false); }
  };

  const handleVerifyOTP = async () => {
    if (otp.length !== 6) { setError('Enter the 6-digit OTP'); return; }
    if (countdown === 0) { setError('OTP has expired. Please request a new one.'); return; }
    setLoading(true); setError('');
    try {
      const res = await verifyOTP(phone, otp);
      onLogin(
        { name: res.data.landlord_name, id: res.data.landlord_id, referral_code: res.data.referral_code || '' },
        res.data.access_token
      );
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid OTP');
    } finally { setLoading(false); }
  };

  const formatCountdown = (secs) => {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const timerClass = countdown === 0 ? 'expired' : countdown < 60 ? 'warning' : 'active';

  return (
    <>
      <style>{styles}</style>
      <div className="login-root">

        {/* ── LEFT PANEL ── */}
        <div className="login-left">
          <div className="left-logo">
            <img src="/Hashvi_logo_white_no_BG.svg" alt="Hashvi Rental" />
          </div>

          <div className="left-content">
            <div className="left-tag">
              <span className="left-tag-dot" />
              <span className="left-tag-text">Now live in Bangalore</span>
            </div>
            <h2 className="left-h2">
              Rent collected.<br />
              Receipts sent.<br />
              <span>Nothing else to do.</span>
            </h2>
            <p className="left-sub">
              Hashvi Rental handles reminders, reads payment screenshots and generates receipts — automatically on WhatsApp.
            </p>
            <ul className="left-features">
              {[
                'No app for tenants — they pay on GPay as usual',
                'Automated reminders before & after due date',
                'Professional PDF receipts sent instantly',
                'Multi-property management from one dashboard',
              ].map((f, i) => (
                <li className="left-feat" key={i}>
                  <span className="feat-check">✓</span>
                  {f}
                </li>
              ))}
            </ul>
          </div>

          <div className="left-footer">
            © 2026 Hashvi Ventures Private Limited · Bangalore
          </div>
        </div>

        {/* ── RIGHT PANEL ── */}
        <div className="login-right">
          <div className="login-card">

            {/* Step indicator */}
            <div className="step-indicator">
              <div className={`step-dot ${step === 'phone' ? 'active' : 'done'}`} />
              <div className={`step-dot ${step === 'otp' ? 'active' : ''}`} />
              <span className="step-label">{step === 'phone' ? 'Enter number' : 'Verify OTP'}</span>
            </div>

            <div className="card-header">
              <div className="card-title">
                {step === 'phone' ? 'Welcome back 👋' : 'Check WhatsApp 📱'}
              </div>
              <div className="card-sub">
                {step === 'phone'
                  ? 'Enter your registered WhatsApp number to login.'
                  : <>OTP sent to <strong>+91 {phone}</strong>. Enter the 6-digit code below.</>
                }
              </div>
            </div>

            {step === 'phone' ? (
              <>
                <label className="field-label">WhatsApp Number</label>
                <div className="input-wrap" style={{ marginBottom: 20 }}>
                  <span className="input-prefix-flag">🇮🇳 +91 <span className="input-divider" /></span>
                  <input
                    className="text-input"
                    type="tel"
                    placeholder="10-digit number"
                    value={phone}
                    maxLength={10}
                    onChange={e => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                    onKeyDown={e => e.key === 'Enter' && handleRequestOTP()}
                  />
                </div>
                <button className="btn-primary" onClick={handleRequestOTP} disabled={loading || phone.length < 10}>
                  {loading ? 'Sending OTP...' : 'Get OTP on WhatsApp →'}
                </button>
                <hr className="divider" />
                <div className="register-row">
                  New to Hashvi?{' '}
                  <button className="register-link" onClick={onRegister}>Request early access</button>
                </div>
              </>
            ) : (
              <>
                <div className="wa-hint">
                  <span className="wa-hint-icon">💬</span>
                  <span className="wa-hint-text">Check your WhatsApp for a message from Hashvi Rental with your 6-digit OTP.</span>
                </div>

                <label className="field-label">6-Digit OTP</label>
                <div className="input-wrap" style={{ marginBottom: 8 }}>
                  <input
                    className="text-input otp-input"
                    type="number"
                    placeholder="——————"
                    value={otp}
                    onChange={e => setOtp(e.target.value.slice(0, 6))}
                    onKeyDown={e => e.key === 'Enter' && handleVerifyOTP()}
                  />
                </div>

                <div className={`otp-timer ${timerClass}`}>
                  {countdown > 0
                    ? `OTP expires in ${formatCountdown(countdown)}`
                    : '⚠️ OTP expired — please request a new one'
                  }
                </div>

                <button
                  className="btn-primary"
                  onClick={handleVerifyOTP}
                  disabled={loading || countdown === 0 || otp.length < 6}
                >
                  {loading ? 'Verifying...' : 'Login to Hashvi →'}
                </button>

                <button
                  className="btn-secondary"
                  onClick={handleResendOTP}
                  disabled={resendLoading || countdown > 540}
                >
                  {resendLoading ? 'Resending...' :
                    countdown > 540
                      ? `Resend OTP in ${formatCountdown(countdown - 540)}`
                      : '🔄 Resend OTP'
                  }
                </button>

                <button
                  className="btn-ghost"
                  onClick={() => { setStep('phone'); setError(''); setOtp(''); setCountdown(0); }}
                >
                  ← Change number
                </button>
              </>
            )}

            {error && <div className="error-box">⚠️ {error}</div>}
          </div>
        </div>
      </div>
    </>
  );
}
